import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import logo from '../assets/logo.svg';

interface InviteInfo {
  workspaceName: string;
  inviterName: string;
  inviterEmail: string;
  role: string;
  email: string;
  expiresAt: string;
}

/**
 * Defense-in-depth check for an absolute URL the FE is about to navigate to.
 * Both the server-derived `destination` and the invite `returnTo` are
 * produced/validated server-side; this re-validates client-side before we
 * ever set window.location, so a misconfigured or tampered response can never
 * bounce the user to an off-suite origin. Allowed: https on a LevelUp-owned
 * suite domain (roilevelup.com — incl. staging subdomains — or the levelup.dev
 * dev domain used by non-prod app registry defaults).
 */
export function isTrustedSuiteUrl(candidate: string | null | undefined): boolean {
  if (!candidate) return false;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'https:') return false;
    const host = url.hostname;
    return (
      host === 'roilevelup.com' ||
      host.endsWith('.roilevelup.com') ||
      host === 'levelup.dev' ||
      host.endsWith('.levelup.dev')
    );
  } catch {
    return false;
  }
}

/** A Central-relative, same-origin path ("/…") — but NOT a protocol-relative
 * "//host" URL, which the browser would treat as an off-origin navigation. */
export function isSafeRelativePath(candidate: string | null | undefined): boolean {
  return !!candidate && candidate.startsWith('/') && !candidate.startsWith('//');
}

/**
 * Choose where to send the user after a successful invite acceptance.
 *
 * The canonical rule lives SERVER-SIDE (see inviteDestinationService): the
 * user goes to the app the invite originated from — originatingAppId outranks
 * returnTo, which outranks a single granted app, which outranks the Central
 * workspace landing. The backend returns the resolved `destination` (an
 * absolute app URL or a Central-relative path). The FE only navigates + guards:
 *
 *   1. `destination` from the backend:
 *        - a Central-relative path ("/…") → same-origin, navigate.
 *        - an absolute URL → must pass the trusted-suite-origin check
 *          (defense in depth vs a tampered/misconfigured response).
 *   2. Legacy fallback (older backend that has not yet populated
 *      `destination`): validated `returnTo`, then workspace settings, then
 *      `/settings/general`.
 */
export function pickPostAcceptDestination(
  destination: string | null | undefined,
  returnTo: string | null | undefined,
  workspaceId: string | null | undefined
): string {
  if (destination) {
    if (isSafeRelativePath(destination)) {
      return destination;
    }
    if (isTrustedSuiteUrl(destination)) {
      return destination;
    }
    // Absolute but untrusted — ignore and fall through to safe fallbacks.
  }

  // Legacy fallback path (pre-`destination` backend).
  if (isTrustedSuiteUrl(returnTo)) {
    return returnTo as string;
  }

  if (workspaceId) {
    return `/workspace/${workspaceId}/settings/general`;
  }

  return '/settings/general';
}

/**
 * AcceptInvite page — the invite acceptance flow for invited users.
 *
 * Handles:
 * - Validating the invite token
 * - Showing invite details (workspace, role, inviter)
 * - New user: register with name + password
 * - Existing user: sign in with password
 * - On success: store tokens and redirect to the originating app or fallback
 */
export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [invalidReason, setInvalidReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Form state
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validate the invite token on mount
  useEffect(() => {
    if (!token) {
      setInvalidReason('No invite token provided');
      setIsLoading(false);
      return;
    }

    async function validate() {
      try {
        const res = await fetch(`/api/auth/invites/validate?token=${encodeURIComponent(token)}`);
        const data = await res.json();

        if (data.ok && data.valid) {
          setInvite(data.invite);
        } else {
          setInvalidReason(data.reason || 'Invalid invite');
        }
      } catch {
        setInvalidReason('Failed to validate invite');
      } finally {
        setIsLoading(false);
      }
    }

    validate();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const body: Record<string, string> = {
        inviteToken: token,
        // Deliberately not sent: the address is masked here and the invite
        // token is authoritative for it server-side. Sending the mask would
        // fail the match; sending anything else would be a mismatch.
        password,
        provider: 'local',
      };

      if (isNewUser && name.trim()) {
        body.name = name.trim();
      }

      const res = await fetch('/api/auth/invites/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        // Handle specific error codes
        if (data.code === 'MISSING_NAME') {
          setIsNewUser(true);
          setError('Please enter your name to create an account');
        } else {
          setError(data.error || 'Failed to accept invite');
        }
        return;
      }

      // Store tokens
      if (data.accessToken) {
        localStorage.setItem('admin_token', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('admin_refresh_token', data.refreshToken);
        }
      }

      setSuccess(true);

      // Pick the best post-acceptance destination:
      //   1. data.destination — server-authoritative launch URL for the app
      //      the invitee was invited to (registry-derived, workspace-scoped).
      //   2. data.returnTo if it's a trusted suite https URL.
      //   3. /workspace/:id/settings/general (workspace-scoped, non-admin).
      //   4. /settings/general (final safety net inside Central).
      const redirectUrl = pickPostAcceptDestination(
        data.destination,
        data.returnTo,
        data.workspaceId
      );

      // Redirect after a brief success message
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1500);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Invalid/expired invite
  if (invalidReason) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invite Unavailable</h1>
          <p className="text-gray-500 mb-6">{invalidReason}</p>
          <Link to="/auth/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Welcome!</h1>
          <p className="text-gray-500">You've joined <strong>{invite?.workspaceName}</strong>. Redirecting...</p>
        </div>
      </div>
    );
  }

  // Main acceptance form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src={logo} alt="LevelUp" className="h-8 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900">
            {isNewUser ? 'Create your account to join' : "You're invited to join"}
          </h1>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {invite?.workspaceName}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Invited by {invite?.inviterName} as <span className="font-medium capitalize">{invite?.role}</span>
          </p>
          {invite?.email && (
            <p className="text-xs text-gray-400 mt-1">
              This invite was sent to {invite.email}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            {/* PREFILLED AND LOCKED.
                An invitation is bound to one address, so a free-text field
                only ever invited a mismatch the server then had to reject.
                The address is the invite's own, and the server independently
                derives it from the token — so this cannot be worked around by
                editing the DOM. */}
            <input
              id="email"
              type="email"
              value={invite?.email ?? ''}
              readOnly
              disabled
              aria-describedby="email-locked-hint"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
            <p id="email-locked-hint" className="mt-1 text-xs text-gray-500">
              This invitation can only be accepted with this address.
            </p>
          </div>

          {/* Name (new users) */}
          {isNewUser && (
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {isNewUser ? 'Create Password' : 'Password'}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isNewUser ? 'Create a password' : 'Enter your password'}
              required
              minLength={8}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !invite?.email || !password}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Joining...' : isNewUser ? 'Create Account & Join' : 'Sign In & Join'}
          </button>
        </form>

        {/* Toggle new/existing user */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setIsNewUser(!isNewUser)}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {isNewUser ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
          </button>
        </div>
      </div>
    </div>
  );
}
